import { useState } from 'react';
import { WEB_ICO } from '../shell/icons';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { useAsync } from '../hooks/useAsync';
import { contentApi } from '../api/content';
import { DEFAULT_COURSE_ID } from '../constants';
import type { UnitDetail } from '../api/content.types';
import type { FlashcardMode } from '../screens/library/types';

/// Bài Minna (1..25) đứng sau 3 unit nền tảng (order 0..2) — khớp
/// BAI_PATH_ORDER_OFFSET ở apps/api/src/progress/progress.service.ts.
const FIRST_BAI_ORDER = 3;

interface LibraryUnit {
  id: string;
  name: string;
  order: number;
  /// Số từ vựng = tổng itemCount các lesson STANDARD track VOCAB.
  vocabCount: number;
  /// Số mẫu ngữ pháp = tổng itemCount các lesson STANDARD track GRAMMAR.
  grammarCount: number;
  /// Số kanji của Bài, lấy từ UnitDetail.kanjiCount (kanji không nằm trong lesson).
  kanjiCount: number;
}

/// Chỉ tính lesson STANDARD — lesson PRACTICE không có item.
function sumItems(unit: UnitDetail, track: 'VOCAB' | 'GRAMMAR'): number {
  return unit.lessons.filter((l) => l.mode === 'STANDARD' && l.track === track).reduce((sum, l) => sum + l.itemCount, 0);
}

const TABS: { mode: FlashcardMode; label: string }[] = [
  { mode: 'kanji', label: 'Kanji' },
  { mode: 'vocab', label: 'Từ vựng' },
  { mode: 'grammar', label: 'Ngữ pháp' },
];

function metaOf(unit: LibraryUnit, mode: FlashcardMode): string {
  if (mode === 'vocab') return `${unit.vocabCount} từ`;
  if (mode === 'kanji') return `${unit.kanjiCount} kanji`;
  return `${unit.grammarCount} mẫu ngữ pháp`;
}

interface Props {
  narrow?: boolean;
  onOpenUnit: (unitId: string, unitName: string, mode: FlashcardMode) => void;
}

export function FlashcardLibraryPage({ narrow = false, onOpenUnit }: Props) {
  const [mode, setMode] = useState<FlashcardMode>('vocab');

  const library = useAsync<LibraryUnit[]>(async () => {
    const course = await contentApi.getCourse(DEFAULT_COURSE_ID);
    const baiUnits = course.units.filter((u) => u.order >= FIRST_BAI_ORDER);
    const details = await Promise.all(baiUnits.map((u) => contentApi.getUnit(u.id)));
    return baiUnits.map((u, i) => ({
      id: u.id,
      name: u.name,
      order: u.order,
      vocabCount: sumItems(details[i], 'VOCAB'),
      grammarCount: sumItems(details[i], 'GRAMMAR'),
      kanjiCount: details[i].kanjiCount,
    }));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, fontFamily: 'var(--font-vi)' }}>
      <div style={{ display: 'flex', gap: 6, background: 'var(--surface-tint)', borderRadius: 'var(--radius-control)', padding: 4, maxWidth: 420 }}>
        {TABS.map((tab) => {
          const active = tab.mode === mode;
          return (
            <button
              key={tab.mode}
              role="tab"
              aria-selected={active}
              onClick={() => setMode(tab.mode)}
              style={{
                flex: 1,
                height: 38,
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-vi)',
                fontSize: 13.5,
                fontWeight: 600,
                background: active ? 'var(--surface-page)' : 'transparent',
                color: active ? 'var(--ink)' : 'var(--text-secondary)',
                boxShadow: active ? 'var(--shadow-raised)' : 'none',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {library.loading ? (
        <LoadingState />
      ) : library.error || !library.data ? (
        <ErrorState message="Không tải được danh sách bài." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
          {library.data.map((unit) => (
            <button
              key={unit.id}
              onClick={() => onOpenUnit(unit.id, unit.name, mode)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-card)',
                padding: 14,
                background: 'var(--surface-page)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-vi)',
              }}
            >
              <span
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 'var(--radius-control)',
                  background: 'var(--surface-tint)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--cta-default)',
                  flexShrink: 0,
                }}
              >
                {unit.order - FIRST_BAI_ORDER + 1}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}>{unit.name}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{metaOf(unit, mode)}</span>
              </span>
              {WEB_ICO.chev()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
